<template>
  <div
    :class="
      `environment-indicator ${
        layout === 'main' ? 'environment-indicator--fixed' : ''
      }`
    "
  >
    <div class="environment-indicator__inner">
      <div class="environment-indicator__inner__top-bar" />
      <div class="environment-indicator__inner__content-wrapper">
        <div class="environment-indicator__inner__content__left-side">
          <img :src="envRibbonTagLeftSrc" alt="" />
        </div>
        <div class="environment-indicator__inner__content">
          <span>{{ $t('test_bar_text1', { env: envName }) }}</span>
          <el-tooltip
            v-model="visibleTooltip"
            :placement="isArabic ? 'bottom-end' : 'bottom-start'"
            :visible-arrow="false"
            popper-class="environment-ribbon-indicator-tooltip-popper"
          >
            <template slot="content">
              <div
                class="tooltip-popper-mask"
                @click="visibleTooltip = false"
              />
              <div class="environment-ribbon-indicator-tooltip-popper__content">
                <div class="environment-ribbon-indicator-tooltip-popper__title">
                  {{ $t('environment_ribbon_tooltip_popper_title') }}
                </div>
                <div
                  class="environment-ribbon-indicator-tooltip-popper__subtitle"
                >
                  {{
                    $t('environment_ribbon_tooltip_popper_subtitle', {
                      env: envName,
                    })
                  }}
                  <el-link
                    type="text"
                    target="_blank"
                    class="contact-support-link"
                    :href="supportUrl"
                  >
                    <div>
                      {{ $t('environment_ribbon_tooltip_popper_contact_us') }}
                    </div>
                  </el-link>
                </div>
              </div>
            </template>
            <svg-icon
              class-name="tooltip-icon"
              icon-class="tooltip-env-ribbon"
            />
          </el-tooltip>
        </div>
        <div class="environment-indicator__inner__content__right-side">
          <img :src="envRibbonTagRightSrc" alt="" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import upperFirst from 'lodash/upperFirst'

import Config from '@/config'
import envRibbonTagLeftSrc from '@/assets/images/env-ribbon-tag-left.svg'
import envRibbonTagRightSrc from '@/assets/images/env-ribbon-tag-right.svg'

export default {
  name: 'EnvironmentIndicator',
  props: {
    layout: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      envRibbonTagLeftSrc,
      envRibbonTagRightSrc,
      visibleTooltip: false,
    }
  },
  computed: {
    ...mapGetters('translations', ['currentLocale']),
    ...mapGetters('profile', { merchantProfile: 'profile' }),
    config() {
      return Config
    },
    isArabic() {
      return this.currentLocale === 'ar_SA'
    },
    envName() {
      return upperFirst(Config.env)
    },
    supportUrl() {
      const queries = []

      if (this.merchantProfile.merchantId) {
        queries.push(`merchant_id=${this.merchantProfile.merchantId}`)
      }

      const queryString = queries.join('&')

      return `${this.$t(
        'zendesk_helper_url_without_hash'
      )}?${queryString}#merchants`
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/mixins.scss';

.environment-indicator {
  position: relative;
  top: 0;
  left: 0;
  z-index: 1000;
  width: 100%;
}

.environment-indicator--fixed {
  position: fixed;

  .environment-indicator__inner {
    &__content-wrapper {
      background-color: transparent;
    }
  }
}

.environment-indicator__inner {
  --tag-height: 37px;
  --tooltip-size: 20px;
  --top-line-height: 9px;
  --ribbon-background-color: #ff9b83;

  position: relative;

  &__top-bar {
    width: 100%;
    height: var(--top-line-height);
    background: var(--ribbon-background-color);
  }

  &__content-wrapper {
    position: absolute;
    top: var(--top-line-heigh);
    left: 50%;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    width: auto;
    height: var(--tag-height);
    background: transparent;
    transform: translate(-50%, 0);
  }

  &__content {
    position: relative;
    display: flex;
    align-items: center;
    height: 100%;
    padding-inline: 0;
    padding-bottom: var(--top-line-height);
    font-size: 15px;
    font-weight: 600;
    line-height: 20px;
    color: #fff;
    text-align: start;
    background: var(--ribbon-background-color);
    border-radius: 0;

    span {
      z-index: 20;
    }

    .tooltip-icon {
      z-index: 20;
      width: var(--tooltip-size);
      height: var(--tooltip-size);
      margin-inline-start: 9px;
      color: #fff;
      cursor: pointer;
    }

    &::before {
      position: absolute;
      top: 0;
      bottom: 0;
      left: -2px;
      z-index: 10;
      width: calc(100% + 4px);
      height: 100%;
      content: '';
      background: var(--ribbon-background-color);
    }

    &__left-side,
    &__right-side {
      position: relative;
      height: var(--tag-height);
    }
  }
}

@include for-rtl {
  .environment-indicator__inner {
    &__content-wrapper {
      flex-direction: row-reverse;
    }
  }
}
</style>

<style lang="scss">
.environment-ribbon-indicator-tooltip-popper {
  padding: 0 !important;
  overflow: hidden;
  border-radius: 12px !important;

  .tooltip-popper-mask {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1993;
    width: 100%;
    min-width: 100vw;
    height: 100%;
    min-height: 100vh;
    background-color: rgba(0, 0, 0, 0.2);
  }

  &__content {
    position: relative;
    z-index: 2024;
    padding: 24px;
    color: #000 !important;
    background: #fff !important;
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
    text-align: start;
  }

  &__subtitle {
    margin-top: 10px;
    font-size: 14px;
    font-weight: 400;
    line-height: 23px;
    color: #666;
    text-align: start;

    .contact-support-link {
      color: #8e64aa;
      text-decoration: underline;
      text-underline-offset: 3px;
    }
  }
}
</style>
